import { inject, Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';

@Injectable({ providedIn: 'root' })
export class ViewportDiagnosisService {
  private logger = inject(NGXLogger);

  runDiagnosis() {
    try {
      const ua = navigator.userAgent;
      const isInAppBrowser =
        /Instagram|FBAN|FBAV|FB_IAB|Messenger|Line|WeChat|Snapchat|Twitter|TikTok|Pinterest|LinkedIn|WhatsApp/i.test(
          ua,
        );

      const visualHeight = window.visualViewport?.height ?? window.innerHeight;
      const visualWidth = window.visualViewport?.width ?? window.innerWidth;
      const innerHeight = window.innerHeight;
      const innerWidth = window.innerWidth;
      const layoutHeight = document.documentElement.clientHeight;
      const layoutWidth = document.documentElement.clientWidth;
      const screenHeight = screen.height;
      const screenWidth = screen.width;

      // Métricas de app-root
      const appRootMetrics = this.getAppRootMetrics();
      if (!appRootMetrics) {
        return null;
      }

      // Diferencias principales (viewport vs app-root)
      const heightDiff = appRootMetrics.boundingClient.height - visualHeight;
      const widthDiff = appRootMetrics.boundingClient.width - visualWidth;

      // Porcentaje de diferencia relativo al viewport
      const heightRatio = heightDiff / visualHeight;

      const screenHeightRatio = innerHeight / screenHeight;

      // Clasificación del caso
      let status: 'ok' | 'overflow' | 'underflow' | 'in-app-screen-bug' = 'ok';

      if (Math.abs(heightDiff) > 10) {
        status = heightDiff > 0 ? 'overflow' : 'underflow';
      }
      if (isInAppBrowser && screenHeightRatio > 0.98) {
        status = 'in-app-screen-bug';
      }

      // Soporte de unidades CSS modernas
      const cssUnitsSupport = this.checkCSSUnitSupport();

      const diagnostics = {
        timestamp: new Date().toISOString(),
        environment: {
          isInAppBrowser,
          userAgent: ua,
          platform: navigator.platform,
          language: navigator.language,
          orientation: screen.orientation?.type ?? 'unknown',
        },
        screen: {
          width: screenWidth,
          height: screenHeight,
          pixelRatio: window.devicePixelRatio,
        },
        viewport: {
          visualHeight,
          visualWidth,
          innerHeight,
          innerWidth,
          layoutHeight,
          layoutWidth,
        },
        appRoot: {
          height: appRootMetrics.boundingClient.height,
          width: appRootMetrics.boundingClient.width,
          computedHeight: appRootMetrics.computedStyle.height,
        },
        comparison: {
          heightDiff,
          widthDiff,
          heightRatio: Number(heightRatio.toFixed(4)),
          screenHeightRatio: Number(screenHeightRatio.toFixed(4)),
        },
        cssUnitsSupport,
        status,
      };

      return diagnostics;
    } catch (error) {
      this.logger.error('[ViewportDiagnostics] Error al ejecutar diagnóstico:', error);

      return null;
    }
  }

  private checkCSSUnitSupport() {
    if (typeof CSS === 'undefined' || typeof CSS.supports !== 'function') {
      return { dvh: null, svh: null, lvh: null };
    }
    return {
      dvh: CSS.supports('height', '100dvh'),
      svh: CSS.supports('height', '100svh'),
      lvh: CSS.supports('height', '100lvh'),
    };
  }

  private getAppRootMetrics() {
    const el = document.querySelector<HTMLElement>('app-root');
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    return {
      boundingClient: {
        width: rect.width,
        height: rect.height,
      },
      computedStyle: {
        height: getComputedStyle(el).height,
      },
    };
  }
}
